/**
 * Issues API
 */

import { buildApiUrl } from '@utils/appBase';

function getStoredAdminUserId(): string | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.sessionStorage.getItem('gardenhub:auth');
		if (!raw) return null;
		const user = JSON.parse(raw) as { id?: string | number } | null;
		return user?.id != null ? String(user.id) : null;
	} catch {
		return null;
	}
}

export type IssueType = 'garden_not_detected' | 'offer_report' | 'plan_report' | 'project_report' | 'user_report';
export type IssueCategory = 'garden_image' | 'offer' | 'plan' | 'project' | 'user';

export interface Issue {
	id: number;
	type: string;
	category?: IssueCategory;
	reporterUserId: number;
	imageUrl: string | null;
	targetType?: string | null;
	targetId?: number | null;
	reasonCode?: string | null;
	message: string | null;
	status: 'open' | 'resolved' | 'dismissed';
	adminNotes?: string | null;
	createdAt: string;
	updatedAt?: string;
	reporterName?: string | null;
	reporterEmail?: string | null;
	offerId?: number | null;
	offerPlanId?: number | null;
	offerPlanTitle?: string | null;
	offerGardenerName?: string | null;
	projectName?: string | null;
	targetUserId?: number | null;
	targetUserName?: string | null;
	targetReportCount?: number;
	handledBy?: number | null;
	handledAt?: string | null;
}

export interface CreateIssuePayload {
	reporter_user_id: number;
	image_url?: string | null;
	message?: string | null;
	type?: IssueType | 'plan_report' | 'project_report' | 'user_report';
	category?: IssueCategory;
	target_type?: 'offer' | 'plan' | 'project' | 'user' | null;
	target_id?: number | null;
	reason_code?: string | null;
}

/**
 * Create a new issue (e.g. "garden not detected" report).
 */
export async function createIssue(payload: CreateIssuePayload): Promise<Issue> {
	const response = await fetch(buildApiUrl('/issues'), {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			reporter_user_id: payload.reporter_user_id,
			image_url: payload.image_url ?? null,
			message: payload.message ?? null,
			type: payload.type ?? 'garden_not_detected',
			category: payload.category ?? null,
			target_type: payload.target_type ?? null,
			target_id: payload.target_id ?? null,
			reason_code: payload.reason_code ?? null
		})
	});
	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.error || 'Failed to submit report');
	}
	return data.data as Issue;
}

/**
 * List issues (admin). Optional filters.
 */
export async function listIssues(params?: { status?: string; type?: string; category?: IssueCategory; query?: string; limit?: number; offset?: number }): Promise<{ items: Issue[]; pagination?: { total: number; limit: number; offset: number; has_more: boolean } }> {
	const search = new URLSearchParams();
	if (params?.status) search.set('status', params.status);
	if (params?.type) search.set('type', params.type);
	if (params?.category) search.set('category', params.category);
	if (params?.query) search.set('query', params.query);
	if (typeof params?.limit === 'number') search.set('limit', String(params.limit));
	if (typeof params?.offset === 'number') search.set('offset', String(params.offset));
	const adminUserId = getStoredAdminUserId();
	if (adminUserId) search.set('userId', adminUserId);
	const query = search.toString();
	const response = await fetch(buildApiUrl(`/issues${query ? `?${query}` : ''}`), {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' }
	});
	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.error || 'Failed to load issues');
	}
	const payload = data.data;
	if (Array.isArray(payload)) {
		return { items: payload as Issue[] };
	}
	return {
		items: (payload?.items ?? []) as Issue[],
		pagination: payload?.pagination
	};
}

/**
 * Update an issue (admin): status and/or admin_notes.
 */
export async function updateIssue(
	id: number,
	updates: { status?: 'open' | 'resolved' | 'dismissed'; admin_notes?: string | null }
): Promise<Issue> {
	const adminUserId = getStoredAdminUserId();
	const response = await fetch(buildApiUrl(`/issues/${id}`), {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			...updates,
			userId: adminUserId ?? undefined
		})
	});
	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.error || 'Failed to update issue');
	}
	return data.data as Issue;
}

export async function applyIssueAction(
	id: number,
	payload: {
		action: 'soft_delete_target' | 'resolve_only' | 'dismiss_only';
		admin_user_id: number;
		reason_code?: string | null;
		reason_message?: string | null;
		issue_status?: 'open' | 'resolved' | 'dismissed';
	}
): Promise<Issue> {
	const response = await fetch(buildApiUrl(`/issues/${id}/action`), {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload)
	});
	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.error || 'Failed to apply issue action');
	}
	return data.data as Issue;
}
